import { http } from '@/api/http';

export interface GameSummary {
  id: number;
  source: 'lichess' | 'chesscom' | 'manual';
  external_id: string;
  time_control: string;
  cadence_bucket: 'bullet' | 'blitz' | 'rapid' | 'classical' | 'correspondence';
  color: 'white' | 'black';
  result: 'win' | 'loss' | 'draw';
  rating_self: number;
  rating_opp: number;
  opening_eco: string | null;
  opening_name: string | null;
  played_at: string;
  imported_at: string;
}

export interface GameDetail extends GameSummary {
  user_id: number;
  pgn: string;
  /** Ply de sortie de répertoire détecté à l'import (migration 013). */
  out_of_book_ply: number | null;
  /** Timestamp ISO du dernier run d'autopsie (migration 026). null = jamais analysée. */
  last_analyzed_at: string | null;
}

export interface MistakePayload {
  ply: number;
  fen_before: string;
  move_played: string;
  move_expected: string;
  eval_drop_cp: number;
  pattern_tags?: string;
  time_pressure?: boolean;
}

export interface StoreMistakesResponse {
  stored: number;
  cards_generated: number;
  by_category: Record<string, number>;
}

/**
 * État autopsie persisté côté backend (fix bug autopsy-not-persisted-on-back-nav).
 *
 * Permet à `AutopsyPage` de réhydrater l'état `done` au remount du composant
 * sans rerunner Stockfish. Si `last_analyzed_at` est null, la partie n'a
 * jamais été analysée → UI en `idle`. Sinon → UI en `done` avec mistakes +
 * outOfBookPly + report restaurés.
 */
export interface GameAutopsyState {
  last_analyzed_at: string | null;
  out_of_book_ply: number | null;
  mistakes: MistakePayload[];
  report: StoreMistakesResponse | null;
}

export interface ImportResponse {
  imported_count: number;
  skipped_count: number;
}

export interface GamesListResponse {
  games: GameSummary[];
  count: number;
  total: number;
}

export interface OpeningLookupResponse {
  repertoire_id: number;
  position_id: number;
  expected_move: string;
  side_to_move: 'white' | 'black';
}

export const gameAutopsyApi = {
  list: (
    params: { cadence?: string; opening_slug?: string; limit?: number; offset?: number } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.cadence) qs.set('cadence', params.cadence);
    if (params.opening_slug) qs.set('opening_slug', params.opening_slug);
    qs.set('limit', String(params.limit ?? 20));
    if (params.offset) qs.set('offset', String(params.offset));
    return http.get<GamesListResponse>(`/api/v1/games?${qs.toString()}`);
  },
  fetch: (id: number) => http.get<GameDetail>(`/api/v1/games/${id}`),
  fetchAutopsy: (id: number) => http.get<GameAutopsyState>(`/api/v1/games/${id}/autopsy`),
  triggerImport: () => http.post<ImportResponse>('/api/v1/games/import', {}),
  storeMistakes: (gameId: number, mistakes: MistakePayload[]) =>
    http.post<StoreMistakesResponse>(`/api/v1/games/${gameId}/mistakes`, { mistakes }),
  lookupFen: async (fen: string): Promise<OpeningLookupResponse | null> => {
    try {
      return await http.get<OpeningLookupResponse>(
        `/api/v1/opening/lookup?fen=${encodeURIComponent(fen)}`,
      );
    } catch (err) {
      if (err && typeof err === 'object' && 'status' in err) {
        const status = (err as { status?: number }).status;
        if (status === 404) return null;
      }
      throw err;
    }
  },
};
